/**
 * Engine diagrams and traction.
 *
 * thebigplan designates each working with a diagram code (G1, P1, W2..., /D
 * suffix = diesel, RCA = the Autocar railcar) and a diagram is a CHAIN of
 * workings one engine performs all day — out and back, out and back. We estimate
 * the same thing: walk the enabled departures in time order and join each to an
 * engine already sat at its origin station (arrival + that station's turnaround),
 * else that departure needs another engine for the day.
 *
 * V3 changes two things:
 *   - a diagram never spans SERVICE GROUPS, because the shuttle unit does not
 *     wander off up the moors; and
 *   - traction is constrained by the group. Steam is not permitted over Network
 *     Rail unless explicitly allowed, which is the regulatory simplification
 *     that makes Mode B attractive in the first place.
 *
 * D1 fix (2026-08): chaining feasibility uses the MEASURED loco turn (15 min,
 * see LOCO_TURN_MIN) and the leg's real arrival when the WTT supplies one —
 * the old 30-minute assumption broke every real 15–20 minute turn and spawned
 * a phantom engine each time (August scored 5 steam diagrams vs a fleet of 3).
 * A diagram is also bounded to MAX_DIAGRAM_SPAN_MIN (footplate hours), and
 * legs carrying an explicit thebigplan diagramId are grouped by decree.
 * Verified against the real ss26 Red pattern: .studiotest/diagrams.test.ts
 * reproduces thebigplan's allocation exactly, leg for leg.
 *
 * 2026-09-11 — NAMED ENGINES. When the Brief's rolling-stock register has
 * engines ticked "Use in simulation", every designated diagram is given a real
 * engine (assignEngines): a diagram that touches Whitby only to an engine that
 * is WHITBY ENABLED, the rest to any engine of the traction, keeping the
 * Whitby-enabled ones free for Whitby where it can. A diagram no engine can
 * take is reported (locoOutOfPlace kind 'no-engine'), never quietly run.
 */
import type { Departure, FleetLoco, LocoDiagram, LocoOutOfPlace, Network, Traction } from './types'
import { MAX_DIAGRAM_SPAN_MIN, groupKeyFor, journeyMiles, journeyMin, locoTurnAt } from './network'
import { fmtMin } from './demand'
import { fireconSteam } from './firecon'
import { engineOutOn } from './fleetAvailability'

/** How a day's traction is designated beyond the Brief's counts. */
export interface DesignateOptions {
  /** A FIRECON day (Chris, 2026-09-11): coal steam is stood down. Every steam
   *  turn runs diesel unless an OIL-FIRED steam engine in the register can take
   *  it (the Whitby rule still applies); without a register no steam runs. */
  firecon?: boolean
  /** THE DATE (YYYY-MM-DD): engines out that day (fleetAvailability.ts — washout
   *  weeks, exams, certificates, active dates) are not in the pool, and a steam
   *  turn no steam engine can take runs DIESEL, marked `swappedFromSteam`. */
  date?: string
}

/**
 * thebigplan writes a dual-traction diagram either way round per working —
 * 1T12 is "P1/D", its return 1T13 is "D/P1", one engine. Canonicalise so both
 * spellings name the same diagram. Use this on ANY diagram code fed into
 * `Departure.diagramId`.
 */
export function canonicalDiagramId(code: string): string {
  return code.split('/').map((s) => s.trim()).sort().join('/')
}

export function estimateDiagrams(net: Network, deps: Departure[]): LocoDiagram[] {
  const diagrams: LocoDiagram[] = []
  const byCode = new Map<string, LocoDiagram>()
  const legs = deps
    .filter((d) => d.enabled && Number.isFinite(d.minutes))
    .sort((a, b) => a.minutes - b.minutes)

  for (const leg of legs) {
    const isAutocar = leg.coaches === 0
    const group = leg.group ?? groupKeyFor(net, leg.o, leg.d)
    // A DIESEL-PINNED empty-stock positioning move is a dedicated light-engine
    // diagram (Chris, 2026-08-27: "ECS moves run diesel") — it never shares a
    // revenue engine's turn. This only affects legs that are BOTH positioning
    // AND traction-pinned, and the pin is set ONLY for a move between the two
    // sheds (bestfit.ts::ecsPinFor). An empty-stock move to or from a station
    // with no shed is left unpinned, so it chains here onto the engine of the
    // passenger train it feeds or that fed it (Chris, 2026-09-07: locomotives
    // may not appear or vanish — the 09:00 empty stock to Whitby is the engine
    // that works the 10:00 back).
    const legEcs = !!leg.positioning && !!leg.tractionPin
    // The WTT's own arrival beats a modelled one: its 15–20 minute turns only
    // chain when the engine is judged ready off the REAL arrival time.
    const arrive = leg.arrMin ?? leg.minutes + journeyMin(net, leg.o, leg.d)
    let g: LocoDiagram | undefined
    if (leg.diagramId) {
      // The real WTT names its engine on every working — group by decree.
      g = byCode.get(leg.diagramId)
      if (!g) {
        g = { station: leg.o, origin: leg.o, lastArrMin: leg.minutes, readyAt: 0, maxCoaches: 0, autocar: isAutocar, legs: [], legIds: [], group, miles: 0, firstDepMin: leg.minutes, code: leg.diagramId }
        byCode.set(leg.diagramId, g)
        diagrams.push(g)
      }
    } else {
      g = diagrams
        .filter((x) => !x.code && x.autocar === isAutocar && x.group === group
          && !!x.ecsPinned === legEcs
          && x.station === leg.o && x.readyAt <= leg.minutes
          // An engine's day is bounded by footplate hours — the reason the real
          // railway rosters a fresh engine for the evening Pullman.
          && arrive - x.firstDepMin <= MAX_DIAGRAM_SPAN_MIN)
        // Tightest fit keeps engines busy and minimises the fleet.
        .sort((a, b) => b.readyAt - a.readyAt)[0]
      if (!g) {
        g = { station: leg.o, origin: leg.o, lastArrMin: leg.minutes, readyAt: 0, maxCoaches: 0, autocar: isAutocar, legs: [], legIds: [], group, miles: 0, firstDepMin: leg.minutes, ecsPinned: legEcs }
        diagrams.push(g)
      }
    }
    g.station = leg.d
    g.lastArrMin = arrive
    g.readyAt = arrive + locoTurnAt(net, leg.d)
    g.maxCoaches = Math.max(g.maxCoaches, leg.coaches)
    g.miles += journeyMiles(net, leg.o, leg.d)
    g.legs.push(`${leg.time} ${net.stations[leg.o].name}→${net.stations[leg.d].name}`)
    g.legIds.push(leg.id)
  }
  return diagrams
}

/** One designated diagram: its traction, and the register engine given to it (when a register is in use). */
export interface DesignatedDiagram {
  diagram: LocoDiagram
  traction: Traction
  engine?: FleetLoco
  /** A steam turn run diesel because no steam engine was available that day. */
  swappedFromSteam?: boolean
}

/** The station Network Rail's segment ends at — Whitby on this railway; -1 when there is none. */
export const whitbyStation = (net: Network): number => {
  const nr = net.segments.find((s) => s.owner === 'networkrail')
  return nr ? nr.to : -1
}

/** Does any leg of this diagram start or end at Whitby (Network Rail)? */
export function diagramTouchesWhitby(net: Network, g: LocoDiagram, byId: Map<string, Departure>): boolean {
  const w = whitbyStation(net)
  if (w < 0) return false
  return g.legIds.some((id) => { const d = byId.get(id); return !!d && (d.o === w || d.d === w) })
}

/**
 * GIVE EVERY DIAGRAM A REAL ENGINE from the register (Chris, 2026-09-11).
 * Rules, in this order: a diagram that touches Whitby takes only an engine of
 * its traction that is WHITBY ENABLED; every other diagram takes any engine of
 * its traction, preferring one that is NOT Whitby enabled so the cleared engines
 * stay free for Whitby; an engine works one diagram a day. Whitby diagrams are
 * assigned first. A diagram left without an engine is returned with `engine`
 * undefined and reported by locoOutOfPlace — never silently run.
 */
export function assignEngines(net: Network, designated: { diagram: LocoDiagram; traction: Traction }[], deps: Departure[], locos: FleetLoco[]): DesignatedDiagram[] {
  const pool = locos.filter((l) => l.inSimulation)
  if (pool.length === 0) return designated.map((d) => ({ ...d }))
  const byId = new Map(deps.map((d) => [d.id, d]))
  const used = new Set<number>()
  const order = designated
    .map((d, i) => ({ d, i, whitby: diagramTouchesWhitby(net, d.diagram, byId) }))
    .sort((a, b) => (Number(b.whitby) - Number(a.whitby)) || (a.d.diagram.firstDepMin - b.d.diagram.firstDepMin) || (a.i - b.i))
  const out: DesignatedDiagram[] = designated.map((d) => ({ ...d }))
  for (const { d, i, whitby } of order) {
    const cands = pool
      .filter((l) => l.traction === d.traction && !used.has(l.id) && (!whitby || l.whitbyEnabled))
      .sort((a, b) => (whitby ? 0 : Number(a.whitbyEnabled) - Number(b.whitbyEnabled)) || (a.sortOrder - b.sortOrder) || (a.id - b.id))
    const pick = cands[0]
    if (pick) { used.add(pick.id); out[i] = { ...d, engine: pick }; out[i].diagram.engineKey = String(pick.id) }
  }
  return out
}

/**
 * Pin-aware traction designation for every engine diagram.
 *
 * A leg's `tractionPin` claims its WHOLE diagram (first pin found wins).
 * Unpinned loco diagrams are filled diesel-first up to the steam:diesel slider
 * count; the Autocar is always a railcar. A diagram whose service group forbids
 * steam is forced to diesel regardless — that is the Network Rail rule, not a
 * preference. Shared by costFromDeps and legLocoCodes so clicking a loco label
 * changes the COSTS as well as the name. With a register in use (`locos`), each
 * diagram is then given a real engine (assignEngines).
 */
export function designateDiagrams(
  net: Network,
  deps: Departure[],
  dieselLoco: number,
  locos?: FleetLoco[],
  opts?: DesignateOptions,
): DesignatedDiagram[] {
  const diagrams = estimateDiagrams(net, deps)
  const byId = new Map(deps.map((d) => [d.id, d]))
  const groupAllows = (key: string, t: Traction): boolean => {
    const g = net.groups.find((x) => x.key === key)
    return !g || g.allowedTraction.includes(t)
  }
  const pinOf = (g: LocoDiagram): Traction | undefined => {
    for (const id of g.legIds) {
      const pn = byId.get(id)?.tractionPin
      if (pn) return pn
    }
    return undefined
  }

  const out: { diagram: LocoDiagram; traction: Traction | 'auto' }[] = diagrams.map((g) => {
    if (g.autocar) return { diagram: g, traction: 'railcar' as Traction }
    const pin = pinOf(g)
    if (pin && groupAllows(g.group, pin)) return { diagram: g, traction: pin }
    // Group forbids steam (Network Rail) -> diesel, not a choice.
    if (!groupAllows(g.group, 'steam')) return { diagram: g, traction: 'diesel' as Traction }
    return { diagram: g, traction: 'auto' }
  })

  const pinnedDiesel = out.filter((r) => r.traction === 'diesel').length
  let remaining = Math.max(0, dieselLoco - pinnedDiesel)
  for (const r of out) {
    if (r.traction !== 'auto') continue
    r.traction = remaining > 0 ? 'diesel' : 'steam'
    if (r.traction === 'diesel') remaining -= 1
  }
  const designated = out as { diagram: LocoDiagram; traction: Traction }[]
  // THE DAY'S FLEET (Chris, 2026-09-11: the fleet factors are constraints inside
  // the build): with a date, an engine out that day — its washout week, an exam,
  // an expired boiler certificate, outside its active dates — is not in the pool.
  const pool = opts?.date && locos ? locos.filter((l) => !engineOutOn(l, opts.date!)) : locos
  const registerInUse = !!pool && pool.some((l) => l.inSimulation)
  /** A steam turn no steam engine can take runs DIESEL — the railway swaps a
   *  diesel in (Chris: "stand down or failure, the engine is swapped with a
   *  diesel") — marked so every surface can say it; one no diesel can take
   *  either stays without an engine and is reported. */
  const swapSteamWithout = (rows: DesignatedDiagram[], withLocos: FleetLoco[], mark: boolean): DesignatedDiagram[] => {
    const settled = rows.map(({ diagram, traction, engine, swappedFromSteam }) => {
      diagram.engineKey = undefined
      if (traction === 'steam' && !engine) return { diagram, traction: 'diesel' as Traction, swappedFromSteam: mark || undefined }
      return { diagram, traction, swappedFromSteam }
    })
    return assignEngines(net, settled, deps, withLocos).map((r, i) => ({ ...r, ...(settled[i].swappedFromSteam ? { swappedFromSteam: true } : {}) }))
  }
  if (opts?.firecon) {
    // FIRECON: coal steam is stood down. Without a register no steam runs at
    // all. With one, a steam turn keeps steam only if an oil-fired engine can
    // take it under the same rules as any other day (one engine per turn, a
    // Whitby turn only to a Whitby-enabled engine); the rest run diesel — the
    // policy of the day, not a swap — and are engined from the diesels.
    const oil = fireconSteam(pool)
    if (oil.length === 0) {
      for (const r of designated) if (r.traction === 'steam') r.traction = 'diesel'
      return registerInUse ? assignEngines(net, designated, deps, pool!) : designated.map((d) => ({ ...d }))
    }
    const locosFc = pool!.filter((l) => l.traction !== 'steam' || l.fuel === 'oil')
    return swapSteamWithout(assignEngines(net, designated, deps, locosFc), locosFc, false)
  }
  if (!registerInUse) return designated.map((d) => ({ ...d }))
  const assigned = assignEngines(net, designated, deps, pool!)
  // A dated designation: a steam turn with no steam engine that day runs diesel, marked.
  if (!opts?.date || !assigned.some((r) => r.traction === 'steam' && !r.engine)) return assigned
  return swapSteamWithout(assigned, pool!, true)
}

/**
 * The engine names the screen and the board paper print, in designation order:
 * the register engine where one is assigned ("92134", "31128 Charybdis"), else
 * the real WTT's own code where it named the engine, else G1/P1/P2… for steam,
 * D/D2… for diesel and R for the railcar, labelled "Steam 1", "Diesel 2",
 * "Railcar". ONE owner — legLocoCodes and locoOutOfPlace both read it, so the
 * engine an issue names is the engine the working turns show.
 */
export function diagramLabels(designated: DesignatedDiagram[]): { code: string; label: string }[] {
  const steamCodes = ['G1', 'P1', 'P2', 'S4', 'S5', 'S6']
  let steamI = 0, dieselI = 0, railcarI = 0
  return designated.map(({ diagram: g, traction, engine }) => {
    if (engine) {
      if (traction === 'railcar') railcarI += 1
      else if (traction === 'diesel') dieselI += 1
      else steamI += 1
      return { code: engine.number, label: engine.name ? `${engine.number} ${engine.name}` : engine.number }
    }
    if (g.code) {
      // The real WTT named this engine — keep its identity on screen.
      if (traction === 'railcar') railcarI += 1
      else if (traction === 'diesel') dieselI += 1
      else steamI += 1
      return { code: g.code, label: g.code }
    }
    if (traction === 'railcar') {
      railcarI += 1
      return { code: 'R', label: railcarI === 1 ? 'Railcar' : `Railcar ${railcarI}` }
    }
    if (traction === 'diesel') {
      dieselI += 1
      return { code: dieselI === 1 ? 'D' : `D${dieselI}`, label: `Diesel ${dieselI}` }
    }
    const code = steamCodes[steamI] ?? `S${steamI + 1}`
    steamI += 1
    return { code, label: `Steam ${steamI}` }
  })
}

/**
 * Per-departure loco code + traction, thebigplan-style: G1/P1/P2 for steam, D
 * for diesel, R for the railcar — or the register engine's number and name
 * when one is assigned. Trains sharing a label are the same engine's day,
 * which is what makes the "next working" chips meaningful.
 */
export function legLocoCodes(
  net: Network,
  deps: Departure[],
  dieselLoco: number,
  locos?: FleetLoco[],
  opts?: DesignateOptions,
): Record<string, { code: string; traction: Traction; label: string; group: string }> {
  const designated = designateDiagrams(net, deps, dieselLoco, locos, opts)
  const names = diagramLabels(designated)
  const out: Record<string, { code: string; traction: Traction; label: string; group: string }> = {}
  designated.forEach(({ diagram: g, traction }, i) => {
    for (const id of g.legIds) out[id] = { code: names[i].code, traction, label: names[i].label, group: g.group }
  })
  return out
}

/**
 * LOCOMOTIVES MAY NOT APPEAR OR VANISH (Chris, 2026-09-07). Every engine starts
 * and ends its day at a shed — Grosmont or Pickering (`net.stables`). A diagram
 * whose first leg departs from a station with no shed is an engine that
 * appeared from nowhere ("Steam 2 begins its day at Whitby with no working to
 * get it there"); one whose last leg arrives at such a station is an engine
 * abandoned there overnight. Whitby, Goathland and Levisham have no shed.
 * With a register in use (`registerInUse`), a diagram no engine could take —
 * a Whitby diagram with no Whitby-enabled engine free, or simply more diagrams
 * than engines — is reported too (kind 'no-engine'). Reported for the review
 * panel, the season table and the board paper — never a silent pass, never
 * quietly costed as though a spare engine were waiting.
 */
export function locoOutOfPlace(net: Network, designated: DesignatedDiagram[], registerInUse = false, deps?: Departure[]): LocoOutOfPlace[] {
  const names = diagramLabels(designated)
  const out: LocoOutOfPlace[] = []
  const byId = new Map((deps ?? []).map((d) => [d.id, d]))
  designated.forEach(({ diagram: g, traction, engine }, i) => {
    if (g.legIds.length === 0) return
    const label = names[i].label
    if (!net.stables.includes(g.origin)) {
      const stn = net.stations[g.origin]?.name ?? String(g.origin)
      out.push({
        label, traction, kind: 'appears', station: g.origin, atMin: g.firstDepMin,
        text: `${label} begins its day at ${stn} with no working to get it there — its first working is the ${fmtMin(g.firstDepMin)}`,
      })
    }
    if (!net.stables.includes(g.station)) {
      const stn = net.stations[g.station]?.name ?? String(g.station)
      out.push({
        label, traction, kind: 'vanishes', station: g.station, atMin: g.lastArrMin,
        text: `${label} ends its day at ${stn} with no working to take it home — its last working arrives at ${fmtMin(g.lastArrMin)}`,
      })
    }
    if (registerInUse && !engine) {
      const whitby = deps ? diagramTouchesWhitby(net, g, byId) : false
      const tr = traction === 'railcar' ? 'railcar' : traction
      out.push({
        label, traction, kind: 'no-engine', station: g.origin, atMin: g.firstDepMin,
        text: whitby
          ? `the ${fmtMin(g.firstDepMin)} diagram touches Whitby and no Whitby-enabled ${tr} engine in the register is free for it — tick another engine Whitby enabled, or take a working out`
          : `the ${fmtMin(g.firstDepMin)} diagram has no ${tr} engine free in the register — every ticked ${tr} engine is already working; add one to the fleet or take a working out`,
      })
    }
  })
  return out
}

/** Each working's NEXT working in its engine diagram (thebigplan's 'Forms'). */
export function chainNext(net: Network, deps: Departure[]): Record<string, string | null> {
  const out: Record<string, string | null> = {}
  for (const g of estimateDiagrams(net, deps)) {
    g.legIds.forEach((id, i) => { out[id] = g.legIds[i + 1] ?? null })
  }
  return out
}
